import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { format } from 'date-fns';

import { devicesApi, positionsApi } from '../../services/api';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

// Fix Leaflet marker icons
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Device {
  id: string;
  imei: string;
  name: string | null;
}

interface PositionPoint {
  deviceTime: string;
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  ignition?: boolean | null;
}

interface TrackStats {
  totalPoints: number;
  distance: number;
  maxSpeed: number;
  avgSpeed: number;
  duration: number;
}

// Playback speed options
const SPEED_OPTIONS = [
  { value: 0.5, label: '0.5x' },
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' },
  { value: 8, label: '8x' },
];

// Component to auto-fit map bounds to track
function FitBounds({ positions }: { positions: PositionPoint[] }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(
        positions.map(p => [p.latitude, p.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [positions, map]);

  return null;
}

// Component to center map on current playback position
function CenterOnPosition({ position, follow }: { position: [number, number]; follow: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (follow && position) {
      map.panTo(position);
    }
  }, [position, follow, map]);

  return null;
}

// Get color based on speed
function getSpeedColor(speed: number | null): string {
  if (speed === null || speed === 0) return '#6b7280'; // Gray for stopped
  if (speed < 30) return '#22c55e'; // Green for slow
  if (speed < 60) return '#eab308'; // Yellow for medium
  if (speed < 100) return '#f97316'; // Orange for fast
  return '#ef4444'; // Red for very fast
}

export function HistoryPage() {
  const [searchParams] = useSearchParams();
  const deviceFromUrl = searchParams.get('device');

  const [selectedDevice, setSelectedDevice] = useState<string>(deviceFromUrl || '');
  const [dateFrom, setDateFrom] = useState<string>(
    format(new Date(Date.now() - 24 * 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm")
  );
  const [dateTo, setDateTo] = useState<string>(
    format(new Date(), "yyyy-MM-dd'T'HH:mm")
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [followMarker, setFollowMarker] = useState(true);
  const [showSpeedColors, setShowSpeedColors] = useState(false);

  // Fetch devices list
  const { data: devicesData } = useQuery({
    queryKey: ['devices', { limit: 100 }],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 100 });
      // All queries under the ['devices', ...] key MUST return Device[] —
      // pages sharing the key receive each other's cached data as-is.
      const data = response.data.data;
      return (Array.isArray(data) ? data : data?.devices || []) as Device[];
    },
  });

  // Fetch track history
  const { data: trackData, isLoading: isLoadingTrack, refetch } = useQuery({
    queryKey: ['track', selectedDevice, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedDevice) return null;
      const response = await positionsApi.getHistory(selectedDevice, {
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo).toISOString(),
      });
      return response.data;
    },
    enabled: !!selectedDevice,
  });

  const positions = (trackData?.data || []) as PositionPoint[];
  const stats = trackData?.stats as TrackStats | undefined;

  // Current position for playback
  const currentPosition = positions[playbackIndex];

  // Calculate interval based on speed
  const playbackInterval = useMemo(() => {
    return Math.floor(500 / playbackSpeed);
  }, [playbackSpeed]);

  // Playback animation
  useEffect(() => {
    if (isPlaying && positions.length > 0) {
      const interval = setInterval(() => {
        setPlaybackIndex(prev => {
          if (prev >= positions.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, playbackInterval);

      return () => clearInterval(interval);
    }
  }, [isPlaying, positions.length, playbackInterval]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (positions.length === 0) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          setIsPlaying(prev => !prev);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setPlaybackIndex(prev => Math.max(0, prev - 1));
          setIsPlaying(false);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setPlaybackIndex(prev => Math.min(positions.length - 1, prev + 1));
          setIsPlaying(false);
          break;
        case 'Home':
          e.preventDefault();
          setPlaybackIndex(0);
          setIsPlaying(false);
          break;
        case 'End':
          e.preventDefault();
          setPlaybackIndex(positions.length - 1);
          setIsPlaying(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [positions.length]);

  const handleSearch = useCallback(() => {
    if (selectedDevice) {
      setPlaybackIndex(0);
      setIsPlaying(false);
      refetch();
    }
  }, [selectedDevice, refetch]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = parseInt(e.target.value);
    setPlaybackIndex(newIndex);
    setIsPlaying(false);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  // Calculate progress percentage
  const progressPercent = positions.length > 0
    ? ((playbackIndex / (positions.length - 1)) * 100).toFixed(1)
    : 0;

  // Create polyline segments with speed colors
  const polylineSegments = useMemo(() => {
    if (!showSpeedColors || positions.length < 2) {
      return [{ positions: positions.map(p => [p.latitude, p.longitude] as [number, number]), color: '#3b82f6' }];
    }

    const segments: { positions: [number, number][]; color: string }[] = [];

    for (let i = 0; i < positions.length - 1; i++) {
      segments.push({
        positions: [
          [positions[i].latitude, positions[i].longitude],
          [positions[i + 1].latitude, positions[i + 1].longitude],
        ],
        color: getSpeedColor(positions[i].speed),
      });
    }

    return segments;
  }, [positions, showSpeedColors]);

  return (
    <div className="h-full flex flex-col">
      {/* Top Controls */}
      <div className="bg-white border-b p-4 flex items-center gap-4 flex-wrap">
        <div>
          <label htmlFor="history-device" className="block text-sm font-medium text-gray-700 mb-1">
            Dispositivo
          </label>
          <select
            id="history-device"
            value={selectedDevice}
            onChange={e => setSelectedDevice(e.target.value)}
            className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Seleccionar...</option>
            {devicesData?.map(device => (
              <option key={device.id} value={device.id}>
                {device.name || device.imei}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="history-from" className="block text-sm font-medium text-gray-700 mb-1">
            Desde
          </label>
          <input
            id="history-from"
            type="datetime-local"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="history-to" className="block text-sm font-medium text-gray-700 mb-1">
            Hasta
          </label>
          <input
            id="history-to"
            type="datetime-local"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="self-end">
          <Button
            onClick={handleSearch}
            disabled={!selectedDevice || isLoadingTrack}
          >
            {isLoadingTrack ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>

        {/* Display options */}
        <div className="self-end flex items-center gap-4 ml-auto">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showSpeedColors}
              onChange={e => setShowSpeedColors(e.target.checked)}
              className="rounded"
            />
            Colores por velocidad
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={followMarker}
              onChange={e => setFollowMarker(e.target.checked)}
              className="rounded"
            />
            Seguir marcador
          </label>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="bg-white border-b p-3 flex items-center gap-6 text-sm">
          <div>
            <span className="text-gray-500">Puntos:</span>{' '}
            <span className="font-medium">{stats.totalPoints}</span>
          </div>
          <div>
            <span className="text-gray-500">Distancia:</span>{' '}
            <span className="font-medium">{stats.distance.toFixed(2)} km</span>
          </div>
          <div>
            <span className="text-gray-500">Vel. Max:</span>{' '}
            <span className="font-medium">{stats.maxSpeed} km/h</span>
          </div>
          <div>
            <span className="text-gray-500">Vel. Prom:</span>{' '}
            <span className="font-medium">{stats.avgSpeed} km/h</span>
          </div>
          <div>
            <span className="text-gray-500">Duracion:</span>{' '}
            <span className="font-medium">{formatDuration(stats.duration)}</span>
          </div>

          {/* Speed legend when colors enabled */}
          {showSpeedColors && (
            <div className="ml-auto flex items-center gap-2 text-xs">
              <span className="text-gray-500">Velocidad:</span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: '#6b7280' }}></span>
                Detenido
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }}></span>
                &lt;30
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: '#eab308' }}></span>
                30-60
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: '#f97316' }}></span>
                60-100
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }}></span>
                &gt;100
              </span>
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        {isLoadingTrack && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-[1000]">
            <LoadingSpinner size="lg" />
          </div>
        )}
        <MapContainer
          center={[18.4861, -69.9312]}
          zoom={12}
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {positions.length > 0 && (
            <>
              <FitBounds positions={positions} />

              {/* Track polyline(s) */}
              {polylineSegments.map((segment, idx) => (
                <Polyline
                  key={idx}
                  positions={segment.positions}
                  color={segment.color}
                  weight={3}
                  opacity={0.8}
                />
              ))}

              {/* Already traveled portion (darker) */}
              {playbackIndex > 0 && (
                <Polyline
                  positions={positions.slice(0, playbackIndex + 1).map(p => [p.latitude, p.longitude])}
                  color="#1e40af"
                  weight={5}
                  opacity={0.9}
                />
              )}

              {/* Start marker */}
              <Marker
                position={[positions[0].latitude, positions[0].longitude]}
                icon={L.divIcon({
                  className: 'custom-marker',
                  html: '<div style="background-color: #22c55e; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                  iconSize: [14, 14],
                  iconAnchor: [7, 7],
                })}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold text-green-600">Inicio del recorrido</p>
                    <p className="mt-1">{format(new Date(positions[0].deviceTime), 'dd/MM/yyyy HH:mm:ss')}</p>
                    <p className="text-gray-500">Velocidad: {positions[0].speed?.toFixed(0) || 0} km/h</p>
                  </div>
                </Popup>
              </Marker>

              {/* End marker */}
              <Marker
                position={[positions[positions.length - 1].latitude, positions[positions.length - 1].longitude]}
                icon={L.divIcon({
                  className: 'custom-marker',
                  html: '<div style="background-color: #ef4444; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                  iconSize: [14, 14],
                  iconAnchor: [7, 7],
                })}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold text-red-600">Fin del recorrido</p>
                    <p className="mt-1">{format(new Date(positions[positions.length - 1].deviceTime), 'dd/MM/yyyy HH:mm:ss')}</p>
                    <p className="text-gray-500">Velocidad: {positions[positions.length - 1].speed?.toFixed(0) || 0} km/h</p>
                  </div>
                </Popup>
              </Marker>

              {/* Playback position marker */}
              {currentPosition && (
                <>
                  <CenterOnPosition
                    position={[currentPosition.latitude, currentPosition.longitude]}
                    follow={followMarker && isPlaying}
                  />
                  <Marker
                    position={[currentPosition.latitude, currentPosition.longitude]}
                    icon={L.divIcon({
                      className: 'custom-marker',
                      html: `
                        <div style="
                          background-color: #3b82f6;
                          width: 20px;
                          height: 20px;
                          border-radius: 50%;
                          border: 3px solid white;
                          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                          transform: rotate(${currentPosition.heading || 0}deg);
                        ">
                          <div style="
                            position: absolute;
                            top: -8px;
                            left: 50%;
                            transform: translateX(-50%);
                            width: 0;
                            height: 0;
                            border-left: 6px solid transparent;
                            border-right: 6px solid transparent;
                            border-bottom: 8px solid #3b82f6;
                          "></div>
                        </div>
                      `,
                      iconSize: [20, 28],
                      iconAnchor: [10, 14],
                    })}
                  >
                    <Popup>
                      <div className="text-sm min-w-40">
                        <p className="font-semibold text-blue-600">Posición actual</p>
                        <div className="mt-2 space-y-1">
                          <p>
                            <span className="text-gray-500">Velocidad:</span>{' '}
                            <span className="font-medium">{currentPosition.speed?.toFixed(0) || 0} km/h</span>
                          </p>
                          <p>
                            <span className="text-gray-500">Dirección:</span>{' '}
                            <span>{currentPosition.heading?.toFixed(0) || 0}°</span>
                          </p>
                          {currentPosition.ignition !== undefined && (
                            <p>
                              <span className="text-gray-500">Ignicion:</span>{' '}
                              <span className={currentPosition.ignition ? 'text-green-600' : 'text-gray-600'}>
                                {currentPosition.ignition ? 'Encendido' : 'Apagado'}
                              </span>
                            </p>
                          )}
                          <p className="pt-1 border-t">
                            {format(new Date(currentPosition.deviceTime), 'dd/MM/yyyy HH:mm:ss')}
                          </p>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                </>
              )}
            </>
          )}
        </MapContainer>
      </div>

      {/* Playback Controls */}
      {positions.length > 0 && (
        <div className="bg-white border-t p-4">
          {/* Timeline slider */}
          <div className="mb-3">
            <input
              type="range"
              min={0}
              max={positions.length - 1}
              value={playbackIndex}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progressPercent}%, #e5e7eb ${progressPercent}%, #e5e7eb 100%)`,
              }}
            />
          </div>

          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Go to start */}
              <button
                onClick={() => {
                  setPlaybackIndex(0);
                  setIsPlaying(false);
                }}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Inicio (Home)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Step back */}
              <button
                onClick={() => {
                  setPlaybackIndex(prev => Math.max(0, prev - 1));
                  setIsPlaying(false);
                }}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Anterior (←)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Play/Pause */}
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                title={isPlaying ? 'Pausar (Espacio)' : 'Reproducir (Espacio)'}
              >
                {isPlaying ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  </svg>
                )}
              </button>

              {/* Step forward */}
              <button
                onClick={() => {
                  setPlaybackIndex(prev => Math.min(positions.length - 1, prev + 1));
                  setIsPlaying(false);
                }}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Siguiente (→)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Go to end */}
              <button
                onClick={() => {
                  setPlaybackIndex(positions.length - 1);
                  setIsPlaying(false);
                }}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Final (End)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>

              {/* Speed selector */}
              <div className="ml-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Velocidad:</span>
                <select
                  value={playbackSpeed}
                  onChange={e => setPlaybackSpeed(parseFloat(e.target.value))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SPEED_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Current timestamp and progress */}
            <div className="flex items-center gap-6 text-sm">
              {currentPosition && (
                <>
                  <div className="text-center">
                    <div className="text-gray-500">Hora</div>
                    <div className="font-medium">
                      {format(new Date(currentPosition.deviceTime), 'HH:mm:ss')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Fecha</div>
                    <div className="font-medium">
                      {format(new Date(currentPosition.deviceTime), 'dd/MM/yyyy')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Velocidad</div>
                    <div className="font-medium">{currentPosition.speed?.toFixed(0) || 0} km/h</div>
                  </div>
                </>
              )}
              <div className="text-center">
                <div className="text-gray-500">Progreso</div>
                <div className="font-medium">
                  {playbackIndex + 1} / {positions.length}
                </div>
              </div>
            </div>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="mt-3 text-xs text-gray-400 text-center">
            Atajos: Espacio = Play/Pausa | ← → = Paso | Home/End = Inicio/Fin
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedDevice && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 pointer-events-none">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <p className="text-gray-600">Seleccione un dispositivo y rango de fechas para ver el historial</p>
          </div>
        </div>
      )}
    </div>
  );
}
